import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import styled from "@emotion/styled";

import AreaTypes from "types/AreaTypes";
import PathTypes from "types/PathTypes";
import PointTypes from "types/PointTypes";

import Paper from "@mui/material/Paper";

import ParkkiTheme from "themes/ParkkiTheme";

import Section from "components/Section";
import SelectALayerInfo from "components/SelectALayerInfo";
import SelectedChipsSection from "components/SelectedChipsSection";
import SelectLayersCollapsibleSection from "components/SelectLayersCollapsibleSection";
import Placeholder from "./Placeholder";

const Container = styled(Paper)`
  padding-top: ${ParkkiTheme.spacings.small};
  font-size: ${ParkkiTheme.fontSizes.xsmall};
`;

const StyledSection = styled(Section)`
  margin-top: 0px;
`;

const TopContainer = styled.div`
  padding-right: ${ParkkiTheme.spacings.small};
  padding-left: ${ParkkiTheme.spacings.small};
  padding-bottom: ${ParkkiTheme.spacings.small};
`;

type Props = {
  loading: boolean;
  areas: AreaTypes[];
  points: PointTypes[];
  paths: PathTypes[];
  onSelectLayers: Function;
};

function SelectedLayersSection({
  loading,
  areas,
  points,
  paths,
  onSelectLayers,
}: Props): JSX.Element {
  const { formatMessage } = useIntl();

  const nbSelectedAreas = useMemo(() => {
    return areas?.filter((area) => area.selected)?.length;
  }, [areas]);

  const nbSelectedPoints = useMemo(() => {
    return points?.filter((point) => point.selected)?.length;
  }, [points]);

  const nbSelectedPaths = useMemo(() => {
    return paths?.filter((path) => path.selected)?.length;
  }, [paths]);

  const nbSelectedLayers = useMemo(() => {
    return nbSelectedAreas + nbSelectedPoints + nbSelectedPaths;
  }, [nbSelectedAreas, nbSelectedPoints, nbSelectedPaths]);

  function handleSelectLayers(
    type: string,
    layers: (AreaTypes | PointTypes | PathTypes)[]
  ) {
    let updatedLayers = layers;

    if (type === "area") {
      updatedLayers = [
        ...updatedLayers,
        ...paths?.filter((layer) => layer.selected),
        ...points.filter((layer) => layer.selected),
      ];
    }

    if (type === "point") {
      updatedLayers = [
        ...updatedLayers,
        ...paths?.filter((layer) => layer.selected),
        ...areas.filter((layer) => layer.selected),
      ];
    }

    if (type === "pathNonTraffic" || type === "pathTraffic") {
      const currentPaths = paths?.filter((layer) => layer.selected) || [];
      const newPaths = layers as PathTypes[];
      const isTrafficSection = type === "pathTraffic";

      if (newPaths.length === 0) {
        const remainingPaths = currentPaths.filter((path) =>
          isTrafficSection
            ? path.apiType !== "TRAFFIC"
            : path.apiType === "TRAFFIC"
        );

        updatedLayers = [
          ...remainingPaths,
          ...points?.filter((layer) => layer.selected),
          ...areas.filter((layer) => layer.selected),
        ];
      } else {
        const otherPaths = currentPaths.filter((path) =>
          isTrafficSection
            ? path.apiType !== "TRAFFIC"
            : path.apiType === "TRAFFIC"
        );

        updatedLayers = [
          ...newPaths,
          ...otherPaths,
          ...points?.filter((layer) => layer.selected),
          ...areas.filter((layer) => layer.selected),
        ];
      }
    }

    return onSelectLayers(updatedLayers);
  }

  if (loading) {
    return (
      <StyledSection>
        <Placeholder height="120px" delay={0} />
      </StyledSection>
    );
  }

  return (
    <StyledSection>
      <Container>
        <TopContainer>
          {nbSelectedAreas > 0 ||
          nbSelectedPoints > 0 ||
          nbSelectedPaths > 0 ? (
            <>
              <SelectedChipsSection
                label={formatMessage({
                  id: "components.selectALayerInfo.areas",
                })}
                layers={areas}
                onSelectLayers={(layers: AreaTypes[]) =>
                  handleSelectLayers("area", layers)
                }
              />
              <SelectedChipsSection
                label={formatMessage({
                  id: "components.selectALayerInfo.points",
                })}
                layers={points}
                color={ParkkiTheme.colors.blue}
                onSelectLayers={(layers: PointTypes[]) =>
                  handleSelectLayers("point", layers)
                }
              />

              <SelectedChipsSection
                label={formatMessage({
                  id: "components.selectALayerInfo.paths",
                })}
                layers={paths.filter((path) => path.apiType !== "TRAFFIC")}
                color={ParkkiTheme.colors.yellow}
                onSelectLayers={(layers: PathTypes[]) => {
                  handleSelectLayers("pathNonTraffic", layers);
                }}
              />

              <SelectedChipsSection
                label={formatMessage({
                  id: "components.selectALayerInfo.pathsTraffic",
                })}
                layers={paths.filter((path) => path.apiType === "TRAFFIC")}
                color={ParkkiTheme.colors.yellow}
                onSelectLayers={(layers: PathTypes[]) =>
                  handleSelectLayers("pathTraffic", layers)
                }
              />
            </>
          ) : (
            <SelectALayerInfo />
          )}
        </TopContainer>

        <SelectLayersCollapsibleSection
          areas={areas}
          points={points}
          paths={paths}
          nbSelected={nbSelectedLayers}
          allSelected={
            nbSelectedLayers === areas?.length + points?.length + paths?.length
          }
          onSelectLayers={handleSelectLayers}
        />
      </Container>
    </StyledSection>
  );
}

SelectedLayersSection.defaultProps = {
  areas: [],
  paths: [],
  points: [],
};

export default SelectedLayersSection;
